import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Avatar,
  Typography,
  Button,
  TextField,
  Box,
  Grid,
  CircularProgress,
  Alert,
  IconButton,
  InputAdornment,
  Tabs,
  Tab,
  Divider,
  Paper,
  Container,
  Snackbar
} from '@mui/material';
import { motion } from 'framer-motion';
import { FaEye, FaEyeSlash, FaUser, FaEnvelope, FaLock } from 'react-icons/fa';
import SideBar from '../Stores/Insight/sideBar';
import axios from 'axios';

const UserProfile = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [toast, setToast] = useState({
    open: false,
    message: '',
    severity: 'success' 
  });

  const [user, setUser] = useState({
    username: '',
    full_name: '',
    email: '',
    hashed_password: '••••••••'
  });

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        setIsLoading(true);

        const savedUsername = localStorage.getItem('savedUsername');
        const savedPassword = localStorage.getItem('savedPassword');

        if (!savedUsername) {
          throw new Error('No username found in local storage');
        }

        if (savedUsername && savedPassword) {
          const response = await axios.post('https://neoe2e.neophyte.live/fm-api/users/login', {
            username: savedUsername,
            hashed_password: savedPassword
          });

          if (!response?.data?.users) {
            throw new Error('Could not load user data');
          }

          const userData = response.data.users;

          if (userData.username !== savedUsername) {
            throw new Error('Username mismatch - authentication failed');
          }

          setUser({
            username: userData.username,
            full_name: userData.full_name,
            email: userData.email,
            hashed_password: '••••••••'
          });
        } else {
          setUser((prev) => ({
            ...prev,
            username: savedUsername
          }));
        }
      } catch (err) {
        console.error('Error fetching user details:', err);
        if (err.message !== 'No username found in local storage') {
          setToast({
            open: true,
            message: 'Failed to load user details',
            severity: 'error'
          });
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserDetails();
  }, []);

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    setToast({ ...toast, open: false });

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setToast({
        open: true,
        message: 'New passwords do not match!',
        severity: 'error'
      });
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      setToast({
        open: true,
        message: 'Password must be at least 8 characters long',
        severity: 'error'
      });
      return;
    }

    try {
      setIsLoading(true);
      const savedUsername = localStorage.getItem('savedUsername');
      if (!savedUsername) {
        throw new Error('No username found in local storage');
      }

      const response = await axios.post('https://neoe2e.neophyte.live/fm-api/users/updatePass', {
        username: savedUsername,
        old_pass: passwordForm.currentPassword,
        new_pass: passwordForm.newPassword
      });

      if (response.data) {
        setToast({
          open: true,
          message: 'Password updated successfully!',
          severity: 'success'
        });
        localStorage.setItem('savedPassword', passwordForm.newPassword);
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        throw new Error(response.data || 'Failed to update password');
      }
    } catch (err) {
      setToast({
        open: true,
        message: err.message || 'Failed to update password',
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords({
      ...showPasswords,
      [field]: !showPasswords[field]
    });
  };

  const handleCloseToast = () => {
    setToast({ ...toast, open: false });
  };

  return (
    <Grid container className="min-h-screen bg-gray-50">
      <Grid item xs={12} md={2}>
        <SideBar />
      </Grid>

      <Grid item xs={12} md={10} className="p-6">
        {isLoading ? (
          <div className="flex justify-center items-center h-screen">
            <CircularProgress className="text-blue-500" size={60} thickness={4} />
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="container mx-auto"
          >
            {/* Toast Notification */}
            <Snackbar
              open={toast.open}
              autoHideDuration={5000}
              onClose={handleCloseToast}
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
              <Alert onClose={handleCloseToast} severity={toast.severity} sx={{ width: '100%' }} elevation={6} variant="filled">
                {toast.message}
              </Alert>
            </Snackbar>

            <Paper elevation={3} className="rounded-2xl overflow-hidden mb-6 bg-white">
              <CardContent>
                <Tabs
                  value={activeTab}
                  onChange={(e, newValue) => setActiveTab(newValue)}
                  indicatorColor="primary"
                  textColor="primary"
                  variant="fullWidth"
                  className="border-b border-gray-200"
                >
                  <Tab
                    label={
                      <span className="flex items-center">
                        <FaUser className="mr-2" />
                        Profile
                      </span>
                    }
                  />
                  <Tab
                    label={
                      <span className="flex items-center">
                        <FaLock className="mr-2" />
                        Change Password
                      </span>
                    }
                  />
                </Tabs>

                <Divider className="my-4" />

                {activeTab === 0 && (
                  <Box className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <motion.div whileHover={{ scale: 1.01 }} transition={{ duration: 0.2 }} className="md:col-span-1">
                      <Card className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 h-full">
                        <CardContent className="p-6 flex flex-col items-center">
                          <Avatar
                            className="w-24 h-24 ring-4 ring-blue-100 mb-4"
                            sx={{
                              bgcolor: '#3B82F6',
                              fontSize: '2.5rem'
                            }}
                          >
                            {user.full_name?.charAt(0)?.toUpperCase() || 'U'}
                          </Avatar>
                          <Typography variant="h5" className="text-gray-800 font-bold mb-1 text-center">
                            {user.full_name || user.username}
                          </Typography>
                          <Typography variant="subtitle1" className="text-blue-500 font-medium mb-4 px-3 py-1 bg-blue-50 rounded-full">
                            {user.username}
                          </Typography>

                          <Button
                            variant="outlined"
                            color="primary"
                            className="mt-4 rounded-lg"
                            onClick={() => setActiveTab(1)}
                            startIcon={<FaLock />}
                          >
                            Change Password
                          </Button>
                        </CardContent>
                      </Card>
                    </motion.div>

                    <motion.div whileHover={{ scale: 1.01 }} transition={{ duration: 0.2 }} className="md:col-span-2">
                      <Card className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 h-full">
                        <CardContent className="p-6">
                          <Typography variant="h6" className="text-gray-800 font-bold mb-4 flex items-center">
                            <FaUser className="mr-2 text-blue-500" />
                            Personal Information
                          </Typography>

                          <Grid container spacing={3} className="mb-4">
                            <Grid item xs={12} sm={6}>
                              <div className="bg-gray-50 p-4 rounded-lg">
                                <Typography className="text-gray-600 font-medium mb-1 flex items-center">
                                  <FaUser className="mr-2 text-blue-500" />
                                  Username
                                </Typography>
                                <Typography className="text-gray-800 font-semibold pl-6">{user.username}</Typography>
                              </div>
                            </Grid>
                            {user.email && (
                              <Grid item xs={12} sm={6}>
                                <div className="bg-gray-50 p-4 rounded-lg">
                                  <Typography className="text-gray-600 font-medium mb-1 flex items-center">
                                    <FaEnvelope className="mr-2 text-blue-500" />
                                    Email
                                  </Typography>
                                  <Typography className="text-gray-800 font-semibold pl-6">{user.email}</Typography>
                                </div>
                              </Grid>
                            )}
                          </Grid>
                        </CardContent>
                      </Card>
                    </motion.div>
                  </Box>
                )}

                {activeTab === 1 && (
                  <Container maxWidth="md">
                    <Card className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
                      <CardContent className="p-8">
                        <Typography variant="h5" className="text-gray-800 font-bold mb-6 flex items-center">
                          <FaLock className="mr-2 text-blue-500" />
                          Change Password
                        </Typography>

                        <form onSubmit={handlePasswordUpdate}>
                          <Grid container spacing={3}>
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                label="Current Password"
                                type={showPasswords.current ? 'text' : 'password'}
                                value={passwordForm.currentPassword}
                                onChange={(e) =>
                                  setPasswordForm({
                                    ...passwordForm,
                                    currentPassword: e.target.value
                                  })
                                }
                                margin="normal"
                                required
                                variant="outlined"
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <FaLock className="text-gray-400" />
                                    </InputAdornment>
                                  ),
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={() => togglePasswordVisibility('current')}
                                        edge="end"
                                      >
                                        {showPasswords.current ? <FaEyeSlash /> : <FaEye />}
                                      </IconButton>
                                    </InputAdornment>
                                  )
                                }}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                label="New Password"
                                type={showPasswords.new ? 'text' : 'password'}
                                value={passwordForm.newPassword}
                                onChange={(e) =>
                                  setPasswordForm({
                                    ...passwordForm,
                                    newPassword: e.target.value
                                  })
                                }
                                margin="normal"
                                required
                                variant="outlined"
                                helperText="Password must be at least 8 characters long"
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <FaLock className="text-gray-400" />
                                    </InputAdornment>
                                  ),
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={() => togglePasswordVisibility('new')}
                                        edge="end"
                                      >
                                        {showPasswords.new ? <FaEyeSlash /> : <FaEye />}
                                      </IconButton>
                                    </InputAdornment>
                                  )
                                }}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                label="Confirm New Password"
                                type={showPasswords.confirm ? 'text' : 'password'}
                                value={passwordForm.confirmPassword}
                                onChange={(e) =>
                                  setPasswordForm({
                                    ...passwordForm,
                                    confirmPassword: e.target.value
                                  })
                                }
                                margin="normal"
                                required
                                variant="outlined"
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <FaLock className="text-gray-400" />
                                    </InputAdornment>
                                  ),
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={() => togglePasswordVisibility('confirm')}
                                        edge="end"
                                      >
                                        {showPasswords.confirm ? <FaEyeSlash /> : <FaEye />}
                                      </IconButton>
                                    </InputAdornment>
                                  )
                                }}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <div className="flex justify-end space-x-3 mt-4">
                                <Button onClick={() => setActiveTab(0)} variant="outlined" color="inherit" className="rounded-lg py-2 px-6">
                                  Cancel
                                </Button>
                                <Button
                                  type="submit"
                                  variant="contained"
                                  color="primary"
                                  className="rounded-lg py-2 px-6"
                                  disabled={isLoading}
                                >
                                  {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Update Password'}
                                </Button>
                              </div>
                            </Grid>
                          </Grid>
                        </form>
                      </CardContent>
                    </Card>
                  </Container>
                )}
              </CardContent>
            </Paper>
          </motion.div>
        )}
      </Grid>
    </Grid>
  );
};

export default UserProfile;
